import React, { useState, useEffect, useLayoutEffect, useRef, useId } from 'react'
import {
    XMarkIcon,
} from '@heroicons/react/24/outline'
import {
    Ruler,
    Disc3,
    AlertOctagon,
    SlidersHorizontal,
    Target,
    Gauge,
    GripHorizontal,
    PanelRightClose,
    PanelRightOpen,
    Weight, CircleGauge, ChevronDown, Thermometer, AlertTriangle, PanelsTopLeft, EyeOff
} from 'lucide-react'
import { Vehicle, type OptimizationInput } from '../../../types/optimization'
import { IsometricPreview } from './IsometricPreview'
import { VehicleFleetOverview } from './VehicleFleetOverview'
import StarRating from '../../ui/StarRating'
import type { VehicleCompletion } from './VehicleConfigurationProgress'
import { AxleConfiguration } from './AxleConfiguration'
import { FifthWheelConfig } from './FifthWheelConfig'
import { FloorPressureConfigurator } from './FloorPressureConfigurator'
import { CGPercentageInput } from './CGPercentageInput'
import { ObstacleEditor } from './ObstacleEditor'
import { MeasurementInput } from '../../MeasurementInput'
import { Measure } from '../../Measurement'
import { optionalVehicleNumber, vehicleInteriorVolume, vehiclePayload, hasVehicleVolumeDifference, roundVehicleVolume, VEHICLE_VOLUME_FRACTION_DIGITS } from '../../../lib/vehicleFormContract'
import { getAllPresets } from '../../../lib/vehicleEquipmentPresets'
import { SECTION_STATE_LABELS, vehicleSectionState } from '../../../lib/vehicleUx'

export interface VehicleConfigRailProps {
    onCompletionChange?: (completion: VehicleCompletion) => void
    vehicle: Vehicle | null | undefined
    tractor?: Vehicle | null | undefined
    coupling?: NonNullable<OptimizationInput['vehicleCombination']>['coupling'] | null
    onChange: (updatedVehicle: Vehicle) => void
    isTractor?: boolean
    /**
     * Contexte d'utilisation :
     * - 'session' (défaut) : Pour OptimizationPage (onglet Équipement), modifications locales à la session d'optimisation.
     * - 'fleet' : Pour VehicleSettings, modifications de configuration de flotte persistées.
     */
    context?: 'session' | 'fleet'
    /** Visual layout is independent of whether edits belong to a load or the fleet. */
    appearance?: 'default' | 'studio' | 'immersive'
    showOverview?: boolean
    equipmentOverlay?: (compact: boolean) => React.ReactNode
    mode?: 'edit' | 'view'
    height?: string | number
    className?: string
    selectedPresetId?: string | null
    initialPanel?: 'preset' | 'dimensions' | 'axles'
    registrationRequired?: boolean
    registrationFocusRequest?: number
    registrationErrorId?: string
    onRequestProfile?: () => void
    allowCustomType?: boolean
    profileTriggerRef?: React.RefObject<HTMLButtonElement>
}

const indicatorTileCls = 'rounded-xl border border-white/70 bg-white/30 p-3 dark:border-slate-700/50 dark:bg-slate-800/25'

function DeviationGauge({ value, showValue = true }: { value: number; showValue?: boolean }) {
    const fraction = Math.max(0, Math.min(1, value / .5))
    const angle = Math.PI * (1 - fraction)
    return <div aria-hidden="true" data-deviation-gauge className="mx-auto w-full max-w-[240px]">
        <svg viewBox={showValue ? '0 0 240 142' : '0 0 240 118'} className="w-full overflow-visible">
            <path d="M 24 108 A 96 96 0 0 1 216 108" fill="none" strokeWidth="12" strokeLinecap="round" className="stroke-slate-200 dark:stroke-slate-700" />
            <path d="M 24 108 A 96 96 0 0 1 216 108" fill="none" pathLength="100" strokeDasharray={`${fraction * 100} 100`} strokeWidth="12" strokeLinecap="round" className="stroke-orange-500" />
            {[0, .25, .5, .75, 1].map(tick => {
                const radians = Math.PI * (1 - tick)
                return <line key={tick} x1={120 + 79 * Math.cos(radians)} y1={108 - 79 * Math.sin(radians)} x2={120 + 85 * Math.cos(radians)} y2={108 - 85 * Math.sin(radians)} strokeWidth="2" className="stroke-slate-400 dark:stroke-slate-500" />
            })}
            <line data-gauge-needle x1="120" y1="108" x2={120 + 68 * Math.cos(angle)} y2={108 - 68 * Math.sin(angle)} strokeWidth="3" strokeLinecap="round" className="stroke-orange-600 dark:stroke-orange-400" />
            <circle cx="120" cy="108" r="6" className="fill-orange-600 dark:fill-orange-400" />
            {showValue && <text x="120" y="138" textAnchor="middle" className="fill-slate-900 text-[22px] font-semibold tabular-nums dark:fill-white">±{Math.round(value * 100)} %</text>}
        </svg>
    </div>
}

function Toggle({ id, checked, onChange, label, description, disabled, indicator = false, icon }: {
    id: string
    checked: boolean
    onChange: (v: boolean) => void
    label: string
    description?: string
    disabled?: boolean
    indicator?: boolean
    icon?: React.ReactNode
}) {
    return (
        <label htmlFor={id} data-equipment-indicator={indicator || undefined} className={`w-full flex items-center justify-between gap-3 ${indicator ? `${indicatorTileCls} focus-within:ring-2 focus-within:ring-orange-500/20` : 'py-1.5'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${disabled && !indicator ? 'opacity-50' : ''}`}>
            <div className="min-w-0">
                <span id={`${id}-label`} className={`flex items-center gap-1.5 ${indicator ? 'text-[11px] font-medium text-slate-500 dark:text-slate-400' : 'text-sm font-medium text-slate-900 dark:text-slate-100'}`}>{icon && <span aria-hidden="true" className="shrink-0 text-orange-500">{icon}</span>}{label}</span>
                {indicator && <span aria-hidden="true" className={`mt-1 block text-[22px] font-semibold tracking-tight ${checked ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>{checked ? 'Oui' : 'Non'}</span>}
                {description && <p id={`${id}-description`} className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">{description}</p>}
            </div>
            <div className="relative flex-shrink-0">
                <input
                    type="checkbox"
                    id={id}
                    aria-labelledby={`${id}-label`}
                    aria-describedby={description ? `${id}-description` : undefined}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => !disabled && onChange(e.target.checked)}
                    className="peer sr-only"
                />
                <div className={`w-9 h-5 rounded-full relative transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-orange-600 peer-focus-visible:ring-offset-2 ${checked ? 'bg-orange-700' : 'bg-slate-500'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm`} style={{ left: checked ? '18px' : '2px' }} />
                </div>
            </div>
        </label>
    )
}

function RangeSlider({
    min,
    max,
    onChange,
    step = 0.5,
    minLimit = -50,
    maxLimit = 50,
    disabled = false,
    unit = 'C',
    indicator = false
}: {
    min: number | string | null
    max: number | string | null
    onChange: (min: number | null, max: number | null) => void
    step?: number
    minLimit?: number
    maxLimit?: number
    disabled?: boolean
    unit?: string
    indicator?: boolean
}) {
    const parseValue = (value: number | string | null): number | null => {
        if (typeof value === 'number' && !isNaN(value)) return value
        if (typeof value === 'string') {
            const parsed = parseFloat(value)
            return Number.isNaN(parsed) ? null : parsed
        }
        return null
    }

    const safeMinVal = parseValue(min) ?? minLimit
    const safeMaxVal = parseValue(max) ?? maxLimit
    const symbol = unit === 'K' ? 'K' : `°${unit}`
    return <div className="space-y-3 py-2">
        <div className="grid grid-cols-2 gap-3">
            {(['min', 'max'] as const).map(bound => {
                const minimum = bound === 'min'
                return <label key={bound} data-measurement-appearance={indicator ? 'indicator' : undefined} className={indicator ? `group relative min-w-0 ${indicatorTileCls} focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-500/15` : 'text-xs text-slate-700 dark:text-slate-200'}>
                    <span className={indicator ? 'mb-2 block pr-4 text-[11px] text-slate-500 dark:text-slate-400' : undefined}>{minimum ? 'Minimum' : 'Maximum'}{!indicator && ` (${symbol})`}</span>
                    <div className="relative">
                        <input type="number" aria-label={`Température ${minimum ? 'minimale' : 'maximale'} précise (${symbol})`} value={minimum ? safeMinVal : safeMaxVal} step={step}
                            min={minimum ? undefined : safeMinVal} max={minimum ? safeMaxVal : undefined} disabled={disabled}
                            onChange={event => {
                                if (event.target.value === '' || !event.currentTarget.validity.valid) return
                                if (minimum) onChange(Number(event.target.value), parseValue(max))
                                else onChange(parseValue(min), Number(event.target.value))
                            }}
                            className={indicator ? 'w-full min-w-0 border-0 bg-transparent p-0 pr-7 text-[22px] font-semibold leading-8 tabular-nums tracking-tight text-slate-900 outline-none dark:text-white disabled:opacity-100' : 'mt-1 w-full min-w-0 rounded-lg border border-slate-300 bg-white p-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white'} />
                        {indicator && <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 flex items-center text-[11px] text-slate-400">{symbol}</span>}
                    </div>
                </label>
            })}
        </div>
        <label className="block text-xs text-slate-700 dark:text-slate-200">Température minimale ({symbol})
            <input type="range" min={Math.min(minLimit, safeMinVal)} max={safeMaxVal} step={step} value={safeMinVal} disabled={disabled}
                aria-valuetext={`${safeMinVal.toLocaleString('fr-FR')} ${symbol}`} onChange={event => onChange(Number(event.target.value), parseValue(max))} className="my-2 block h-6 w-full accent-orange-600" />
            {!indicator && <span aria-hidden="true">{safeMinVal.toLocaleString('fr-FR')} {symbol}</span>}
        </label>
        <label className="block text-xs text-slate-700 dark:text-slate-200">Température maximale ({symbol})
            <input type="range" min={safeMinVal} max={Math.max(maxLimit, safeMaxVal)} step={step} value={safeMaxVal} disabled={disabled}
                aria-valuetext={`${safeMaxVal.toLocaleString('fr-FR')} ${symbol}`} onChange={event => onChange(parseValue(min), Number(event.target.value))} className="my-2 block h-6 w-full accent-orange-600" />
            {!indicator && <span aria-hidden="true">{safeMaxVal.toLocaleString('fr-FR')} {symbol}</span>}
        </label>
    </div>
}

function PanelHeader({ title, icon, onClose, hidden = false }: { title: string; icon: React.ReactNode; onClose: () => void; hidden?: boolean }) {
    if (hidden) return null
    return (
        <div data-panel-header className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-t-2xl">
            <div className="flex items-center gap-2">
                {icon}
                <p className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">{title}</p>
            </div>
            <button
                type="button"
                onClick={onClose}
                aria-label={`Fermer le panneau ${title}`}
                className="rounded-lg p-2 text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
                <XMarkIcon className="w-4 h-4" />
            </button>
        </div>
    )
}

export const VehicleConfigRail: React.FC<VehicleConfigRailProps> = ({
    vehicle,
    tractor,
    coupling,
    onChange,
    isTractor = false,
    context = 'session',
    appearance = context === 'fleet' ? 'studio' : 'default',
    showOverview = true,
    equipmentOverlay,
    mode = 'edit',
    height = '720px',
    className = '',
    selectedPresetId,
    initialPanel,
    registrationRequired = false,
    registrationFocusRequest = 0,
    registrationErrorId,
    onRequestProfile,
    allowCustomType = false,
    profileTriggerRef,
    onCompletionChange,
}) => {
    const [activePanel, setActivePanel] = useState<string | null>(context === 'fleet' ? 'axles' : initialPanel === 'preset' ? null : initialPanel ?? (appearance === 'immersive' ? 'dimensions' : null))
    const [openFleetPanels, setOpenFleetPanels] = useState(['dimensions', 'weights', 'pressure'])
    const [dockTop, setDockTop] = useState(300)
    const [requestedDockHeight, setRequestedDockHeight] = useState<number | null>(null)
    const [selectedObstacleId, setSelectedObstacleId] = useState<string | null>(null)
    const resizeGesture = useRef<{ pointerId: number; y: number; height: number } | null>(null)
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false)
    const [containerSize, setContainerSize] = useState({ width: 1200, height: 720 })
    const [previewSize, setPreviewSize] = useState({ width: 800, height: 500 })
    const previewRef = useRef<HTMLDivElement>(null)
    const immersive = appearance === 'immersive'
    const fleetStudio = appearance === 'studio' || immersive
    const [cardsHidden, setCardsHidden] = useState(false)
    const floatingEquipmentRef = useRef<HTMLDivElement>(null)
    const floatingPanelRef = useRef<HTMLDivElement>(null)
    const initialSizeMeasured = useRef(false)
    const [floatingSizes, setFloatingSizes] = useState({ equipmentWidth: 0, equipmentHeight: 0, panelWidth: 0, panelHeight: 0 })
    const containerRef = useRef<HTMLDivElement>(null)
    const sectionSelectRef = useRef<HTMLSelectElement>(null)
    const restoreNavigationFocus = useRef(false)
    const railId = useId()
    const compactLayout = containerSize.width < (fleetStudio ? 920 : 720) || containerSize.height < (fleetStudio ? 480 : 320)
    const fleetDashboard = fleetStudio && !compactLayout
    const accordionIds = fleetStudio ? ['dimensions', 'weights', 'pressure'] : ['dimensions', 'weights', 'pressure', 'constraints', 'advanced']
    const accordionActive = activePanel != null && accordionIds.includes(activePanel)
    const auxiliaryActive = activePanel != null && !accordionActive
    const visiblePanels = immersive ? (activePanel ? [activePanel] : []) : fleetStudio ? [...openFleetPanels, ...(auxiliaryActive ? [activePanel!] : [])] : activePanel ? [activePanel] : []
    const overviewVisible = fleetDashboard && showOverview
    const sceneLeft = overviewVisible ? 264 : 12
    const sidebarWidth = Math.min(380, Math.round(containerSize.width * 0.38))
    const sceneRight = fleetDashboard ? sidebarWidth + 24 : 12
    const maxDockHeight = Math.max(0, containerSize.height - dockTop - 12)
    const minDockHeight = Math.min(200, maxDockHeight)
    const dockHeight = Math.max(minDockHeight, Math.min(maxDockHeight, requestedDockHeight ?? Math.min(320, containerSize.height * .42)))
    const effectiveDockTop = containerSize.height - dockHeight - 12
    const sceneHidden = !fleetStudio && compactLayout && visiblePanels.length > 0
    const equipmentVisible = !cardsHidden && (!compactLayout || !activePanel)
    const immersiveInsets = {
        left: !compactLayout && equipmentVisible ? floatingSizes.equipmentWidth + 24 : 0,
        right: !compactLayout && !cardsHidden && activePanel ? floatingSizes.panelWidth + 24 : 0,
        top: compactLayout && equipmentVisible ? floatingSizes.equipmentHeight + 24 : 12,
        bottom: compactLayout && !cardsHidden && activePanel ? floatingSizes.panelHeight + 88 : 76,
    }

    useLayoutEffect(() => {
        if (!immersive) return
        const update = () => {
            const next = {
                equipmentWidth: floatingEquipmentRef.current?.offsetWidth || 0,
                equipmentHeight: floatingEquipmentRef.current?.offsetHeight || 0,
                panelWidth: floatingPanelRef.current?.offsetWidth || 0,
                panelHeight: floatingPanelRef.current?.offsetHeight || 0,
            }
            setFloatingSizes(previous => Object.keys(next).every(key => next[key as keyof typeof next] === previous[key as keyof typeof next]) ? previous : next)
        }
        update()
        const observer = new ResizeObserver(update)
        if (floatingEquipmentRef.current) observer.observe(floatingEquipmentRef.current)
        if (floatingPanelRef.current) observer.observe(floatingPanelRef.current)
        return () => observer.disconnect()
    }, [immersive, compactLayout, equipmentVisible, activePanel, cardsHidden])

    useEffect(() => { if (!fleetDashboard) resizeGesture.current = null }, [fleetDashboard])

    useLayoutEffect(() => {
        if (!overviewVisible) return
        const container = containerRef.current
        const overview = container?.querySelector<HTMLElement>('[data-equipment-overview]')
        if (!container || !overview) return
        const updateDock = () => setDockTop(Math.max(Math.min(220, container.clientHeight * .45), Math.ceil(overview.getBoundingClientRect().bottom - container.getBoundingClientRect().top) + 12))
        updateDock()
        const observer = new ResizeObserver(updateDock)
        observer.observe(overview)
        observer.observe(container)
        return () => observer.disconnect()
    }, [overviewVisible])

    useEffect(() => {
        const element = previewRef.current
        if (!element || !fleetStudio) return
        const observer = new ResizeObserver(() => setPreviewSize({ width: Math.max(1, element.clientWidth), height: Math.max(1, element.clientHeight) }))
        observer.observe(element)
        return () => observer.disconnect()
    }, [fleetStudio])

    useEffect(() => {
        if (!restoreNavigationFocus.current) return
        restoreNavigationFocus.current = false
        if (compactLayout) sectionSelectRef.current?.focus()
        else document.getElementById(`${railId}-nav-${activePanel ?? 'dimensions'}`)?.focus()
    }, [compactLayout, railId, activePanel])

    useEffect(() => {
        setActivePanel(previous => previous === 'obstacles' && isTractor || previous === 'preset' && context !== 'fleet' ? null : previous)
    }, [isTractor, context])

    useEffect(() => {
        if (!fleetStudio || !compactLayout) return
        const frame = requestAnimationFrame(() => {
            const focused = document.activeElement as HTMLElement | null
            const target = focused?.closest('[data-config-panel], [data-equipment-card]') && containerRef.current?.contains(focused)
                ? focused : activePanel ? document.getElementById(`${railId}-nav-${activePanel}`) : null
            target?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        })
        return () => cancelAnimationFrame(frame)
    }, [fleetStudio, compactLayout, containerSize.width, containerSize.height, activePanel, railId])

    useEffect(() => {
        let wasCompact = false
        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect()
                const nextCompact = rect.width < (fleetStudio ? 920 : 720) || rect.height < (fleetStudio ? 480 : 320)
                if (!initialSizeMeasured.current) {
                    initialSizeMeasured.current = true
                    if (immersive && nextCompact) setActivePanel(null)
                }
                const focused = document.activeElement as HTMLElement | null
                if (wasCompact !== nextCompact && focused?.closest('[data-rail-navigation], [data-panel-resize-handle]')) restoreNavigationFocus.current = true
                wasCompact = nextCompact
                const focusedPanel = focused?.closest<HTMLElement>('[data-config-panel]')?.dataset.configPanel
                if ((!fleetStudio || nextCompact) && focusedPanel && containerRef.current.contains(focused)) {
                    // Keep the section being edited visible when the available viewport shrinks.
                    setActivePanel(focusedPanel)
                }
                setContainerSize({
                    width: Math.max(1, rect.width),
                    height: Math.max(1, rect.height),
                })
            }
        }

        updateSize()
        const resizeObserver = new ResizeObserver(updateSize)
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
        }
        window.addEventListener('resize', updateSize)
        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', updateSize)
        }
    }, [fleetStudio, immersive])

    const keepInvalidDraft = (panels: string[]) => {
        const invalid = Array.from(containerRef.current?.querySelectorAll<HTMLInputElement>('[data-config-panel] input') || []).find(input => panels.includes(input.closest<HTMLElement>('[data-config-panel]')?.dataset.configPanel || '') && (input.hasAttribute('data-measurement-input') || input.type === 'number') && !input.disabled && !input.validity.valid)
        if (!invalid) return false
        invalid.reportValidity()
        invalid.focus()
        return true
    }

    const togglePanel = (id: string, restoreFocus = true) => {
        const closing = visiblePanels.includes(id)
        if (keepInvalidDraft(visiblePanels)) return
        if (fleetStudio && !immersive && accordionIds.includes(id)) {
            setOpenFleetPanels(previous => closing ? previous.filter(panel => panel !== id) : [...previous, id])
            if (compactLayout) setActivePanel(id)
            return
        }
        setActivePanel(previous => closing ? previous === id ? null : previous : id)
        if (closing && restoreFocus) requestAnimationFrame(() => {
            if (compactLayout) sectionSelectRef.current?.focus()
            else document.getElementById(`${railId}-nav-${id}`)?.focus()
        })
    }

    const selectImmersivePanel = (id: string | null, restoreFocus = false) => {
        if (keepInvalidDraft(visiblePanels)) return
        setCardsHidden(false)
        setActivePanel(id)
        if (restoreFocus) requestAnimationFrame(() => {
            if (compactLayout) sectionSelectRef.current?.focus()
            else document.getElementById(`${railId}-nav-${activePanel}`)?.focus()
        })
    }

    // Deduce values safely from vehicle prop
    // Fleet fields are persisted flat; preview defaults must never enter the form.
    const vLength = context === 'fleet' ? optionalVehicleNumber(vehicle?.length) : optionalVehicleNumber(vehicle?.dimensions?.length ?? vehicle?.length) ?? 12000
    const vWidth = context === 'fleet' ? optionalVehicleNumber(vehicle?.width) : optionalVehicleNumber(vehicle?.dimensions?.width ?? vehicle?.width) ?? 2500
    const vHeight = context === 'fleet' ? optionalVehicleNumber(vehicle?.height) : optionalVehicleNumber(vehicle?.dimensions?.height ?? vehicle?.height) ?? 2600
    const interiorVolume = isTractor ? null : vehicleInteriorVolume(vLength, vWidth, vHeight)
    const declaredVolume = roundVehicleVolume((vehicle as any)?.capacity_volume)
    const hasVolumeDifference = !isTractor && hasVehicleVolumeDifference(interiorVolume, declaredVolume)
    const vEmptyWeight = (vehicle as any)?.empty_weight ?? null
    const vGrossWeight = (vehicle as any)?.max_weight_permitted ?? null
    const vCombinationWeight = (vehicle as any)?.max_combination_weight ?? null
    const isContainer = vehicle?.type === 'CONTAINER'
    const derivedPayload = isTractor ? null : vehiclePayload(vGrossWeight, vEmptyWeight)
    const declaredPayload = optionalVehicleNumber(vehicle?.capacity_weight)
    const hasPayloadDifference = !isTractor && declaredPayload != null && (derivedPayload == null || Math.abs(derivedPayload - declaredPayload) > 0.01)
    const vMaxFloorPressure = context === 'fleet' ? optionalVehicleNumber((vehicle as any)?.max_floor_pressure) : (vehicle as any)?.max_floor_pressure ?? (vehicle as any)?.constraints?.maxFloorPressure ?? null
    const vPressureCellSize = context === 'fleet' ? optionalVehicleNumber((vehicle as any)?.pressure_cell_target_size) : (vehicle as any)?.pressure_cell_target_size ?? (vehicle as any)?.constraints?.pressureCellTargetSize ?? null
    const vHasReefer = !!((vehicle as any)?.has_reefer_unit ?? (vehicle as any)?.hasReeferUnit)
    const vReeferRange = (vehicle as any)?.reefer_temperature_range || { min: -20, max: 10, unit: 'C' }
    const vIsAdr = !!((vehicle as any)?.is_hazardous_compatible)

    const cgX = vehicle?.constraintEngineConfig?.targetCG?.x ?? vehicle?.target_cg_x ?? 0.5
    const cgY = vehicle?.constraintEngineConfig?.targetCG?.y ?? vehicle?.target_cg_y ?? 0.5
    const maxCGDev = (vehicle?.constraintEngineConfig as any)?.thresholds?.maxCenterOfGravityDeviation ?? (vehicle as any)?.max_cg_deviation ?? 0.1

    // Handle vehicle state mutation updates
    const handleUpdate = (updates: Partial<Vehicle> | Record<string, any>) => {
        if (!vehicle || mode === 'view') return
        const updatedVehicle = { ...vehicle, ...updates } as Vehicle
        onChange(updatedVehicle)
    }

    const handleGrossMassChange = (gross: number | null) => {
        const nextGross = gross == null ? null : Number(gross)
        const nextPayload = isTractor ? null : vehiclePayload(nextGross, vEmptyWeight)
        handleUpdate({
            max_weight_permitted: nextGross,
            mass_contract_version: 'vehicle_mass_contract.v1',
            capacity_weight: isTractor ? null : nextPayload,
        })
    }

    const handleTareChange = (tare: number | null) => {
        const nextTare = tare == null ? null : Number(tare)
        const nextPayload = isTractor ? null : vehiclePayload(vGrossWeight, nextTare)
        handleUpdate({
            empty_weight: nextTare,
            mass_contract_version: 'vehicle_mass_contract.v1',
            capacity_weight: isTractor ? null : nextPayload,
        })
    }

    const handleDimChange = (dim: 'length' | 'width' | 'height', val: number | null) => {
        if (!vehicle) return
        const newLength = dim === 'length' ? (context === 'fleet' ? val : val ?? vLength) : vLength
        const newWidth = dim === 'width' ? (context === 'fleet' ? val : val ?? vWidth) : vWidth
        const newHeight = dim === 'height' ? (context === 'fleet' ? val : val ?? vHeight) : vHeight

        onChange({
            ...vehicle,
            length: newLength,
            width: newWidth,
            height: newHeight,
            ...(context === 'fleet' ? { capacity_volume: isTractor ? null : vehicleInteriorVolume(newLength, newWidth, newHeight) } : {}),
            dimensions: {
                length: newLength,
                width: newWidth,
                height: newHeight,
            },
        } as unknown as Vehicle)
    }

    const handleCGChange = (field: 'x' | 'y' | 'deviation', val: number) => {
        if (!vehicle || mode === 'view') return
        const newX = field === 'x' ? val : cgX
        const newY = field === 'y' ? val : cgY
        const newDev = field === 'deviation' ? val : maxCGDev

        onChange({
            ...vehicle,
            target_cg_x: newX,
            target_cg_y: newY,
            max_cg_deviation: newDev,
            constraintEngineConfig: {
                ...(vehicle.constraintEngineConfig ?? {}),
                targetCG: {
                    ...(vehicle.constraintEngineConfig?.targetCG ?? {}),
                    x: newX,
                    y: newY,
                    z: vehicle.constraintEngineConfig?.targetCG?.z ?? 0.5,
                },
                thresholds: {
                    ...((vehicle.constraintEngineConfig as any)?.thresholds ?? {}),
                    maxCenterOfGravityDeviation: newDev,
                },
            },
        } as Vehicle)
    }

    // Panel completion status
    const getPanelStatus = (panelId: string) => vehicleSectionState({ ...vehicle, length: vLength, width: vWidth, height: vHeight, max_weight_permitted: vGrossWeight, empty_weight: vEmptyWeight }, fleetStudio && panelId === 'dimensions' ? 'size' : fleetStudio && panelId === 'constraints' ? 'equipment' : panelId, registrationRequired, isTractor)

    const menuItems = [
        { id: 'dimensions', label: fleetStudio ? (isTractor ? 'Dimensions du châssis' : 'Dimensions intérieures utiles') : 'Dimensions & Poids', icon: Ruler },
        ...(fleetStudio ? [{ id: 'weights', label: 'Capacités & Poids', icon: Weight }, { id: 'pressure', label: 'Pression plancher', icon: CircleGauge }] : []),
        { id: 'axles', label: 'Essieux', icon: Disc3 },
        ...(fleetStudio ? [{ id: 'advanced', label: 'Centre de gravité cible', icon: Target }] : []),
        ...(isTractor ? [] : [{ id: 'obstacles', label: 'Obstacles', icon: AlertOctagon }]),
        ...(!fleetStudio || context === 'session' ? [{ id: 'constraints', label: fleetStudio ? 'Compatibilités' : 'Contraintes & Équip.', icon: SlidersHorizontal }] : []),
        ...(!fleetStudio ? [{ id: 'advanced', label: 'Paramètres avancés', icon: Target }] : []),
    ]

    const completionItems = fleetStudio ? [...menuItems, { id: 'preset' }, ...(!menuItems.some(item => item.id === 'constraints') ? [{ id: 'constraints' }] : [])] : menuItems
    const completedCount = completionItems.filter(item => {
        const s = getPanelStatus(item.id)
        return s === 'complete'
    }).length

    const inputCls = `w-full min-w-0 px-3 py-2 rounded-lg text-sm tabular-nums outline-none transition focus:ring-2 focus:border-orange-500 focus:ring-orange-500/20 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white disabled:opacity-60`
    const measurementAppearance = fleetStudio ? { unitPlacement: 'suffix' as const, labelClassName: 'mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400' } : {}
    const indicatorAppearance = fleetStudio ? { ...measurementAppearance, showEditIcon: false, variant: 'indicator' as const, labelClassName: 'mb-2 block pr-4 text-[11px] font-medium leading-4 text-slate-500 dark:text-slate-400', placeholder: '—' } : measurementAppearance
    const indicatorInputCls = fleetStudio ? 'w-full min-w-0 rounded-md border-0 bg-transparent p-0 text-[22px] font-semibold leading-8 tabular-nums tracking-tight text-slate-900 outline-none dark:text-white disabled:opacity-100' : inputCls
    const statusLabels = SECTION_STATE_LABELS
    const applicableSections = completionItems.filter(item => getPanelStatus(item.id) !== 'not-applicable' && item.id !== 'advanced').length
    useEffect(() => { onCompletionChange?.({ completed: completedCount, applicable: applicableSections }) }, [onCompletionChange, completedCount, applicableSections])

    const pressureLimitField = (
        <MeasurementInput {...indicatorAppearance}
            label="Pression max au plancher"
            quantity="surfaceLoad"
            storageUnit="kg/m2"
            value={vMaxFloorPressure}
            onValueChange={v => handleUpdate({ max_floor_pressure: v })}
            min={0}
            className={indicatorInputCls}
            disabled={mode === 'view'}
        />
    )
    const pressureCellField = (
        <>
            <div>
                <MeasurementInput {...indicatorAppearance}
                    label="Taille de cellule de pression"
                    infoLabel="Informations sur la taille de cellule de pression"
                    info="La cellule de pression définit la taille des zones utilisées pour évaluer la répartition au plancher. Une cellule plus petite donne une analyse plus locale ; ce réglage ne remplace pas la limite fournie par le constructeur."
                    quantity="vehicleLength"
                    storageUnit="mm"
                    value={vPressureCellSize}
                    onValueChange={v => handleUpdate({ pressure_cell_target_size: v })}
                    min={100}
                    max={500}
                    className={indicatorInputCls}
                    disabled={mode === 'view'}
                />
            </div>
        </>
    )

    const renderPanel = (panelId: string) => {
        const isLarge = ['axles', 'obstacles'].includes(panelId)
        const isSingleMode = visiblePanels.length === 1
        const accordion = fleetStudio && !immersive && accordionIds.includes(panelId)
        const widthClass = compactLayout ? fleetStudio ? 'w-full min-w-0 shrink-0' : 'h-full w-full min-w-0 flex-1' : fleetStudio ? 'min-w-0 flex-1' : isSingleMode && isLarge ? 'w-[480px]' : 'w-[360px]'

        let panelContent: React.ReactNode = null

        if (panelId === 'dimensions' || fleetStudio && panelId === 'weights') {
            panelContent = (
                <>
                    <PanelHeader hidden={fleetStudio} title="Dimensions & Poids" icon={<Ruler className="w-4 h-4 text-orange-500" />} onClose={() => togglePanel(panelId)} />
                    <div data-panel-body data-vehicle-indicators={fleetStudio ? panelId : undefined} className="p-4 space-y-4 overflow-y-auto flex-1 text-slate-900 dark:text-slate-100">

                        {/* Session keeps the combined form; fleet has two independent cards. */}
                        {panelId !== 'weights' && <div className="rounded-xl p-3.5 space-y-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                            {!fleetStudio && <div className="flex items-center gap-2 mb-1">
                                <Ruler className="w-4 h-4 text-orange-500" />
                                <h3 className="font-semibold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300">{isTractor ? 'Dimensions du châssis' : 'Dimensions intérieures utiles'}</h3>
                            </div>}
                            <div className={fleetStudio && !compactLayout && !immersive ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-2 gap-3'}>
                                <div>
                                    <MeasurementInput {...indicatorAppearance}
                                        label="Longueur"
                                        quantity="vehicleLength"
                                        storageUnit="mm"
                                        value={vLength}
                                        onValueChange={v => handleDimChange('length', v)}
                                        min={1}
                                        className={indicatorInputCls}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                <div>
                                    <MeasurementInput {...indicatorAppearance}
                                        label="Largeur"
                                        quantity="vehicleLength"
                                        storageUnit="mm"
                                        value={vWidth}
                                        onValueChange={v => handleDimChange('width', v)}
                                        min={1}
                                        className={indicatorInputCls}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                <div>
                                    <MeasurementInput {...indicatorAppearance}
                                        label="Hauteur"
                                        quantity="vehicleLength"
                                        storageUnit="mm"
                                        value={vHeight}
                                        onValueChange={v => handleDimChange('height', v)}
                                        min={1}
                                        className={indicatorInputCls}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                {!isTractor && <section data-computed-volume className={fleetStudio ? 'col-span-full rounded-xl border border-orange-200 bg-orange-50/50 p-3 dark:border-orange-900/60 dark:bg-orange-950/20' : undefined}>
                                    <MeasurementInput {...indicatorAppearance}
                                        variant="default"
                                        labelClassName={fleetStudio ? 'mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400' : undefined}
                                        label="Volume intérieur calculé"
                                        hint={context === 'fleet' ? 'Le volume est calculé à partir des dimensions intérieures. Modifier une dimension actualise le volume enregistré ; les obstacles sont traités séparément dans le chargement.' : undefined}
                                        quantity="volume"
                                        storageUnit="m3"
                                        value={interiorVolume}
                                        onValueChange={() => { }}
                                        readOnly
                                        readOnlyFractionDigits={VEHICLE_VOLUME_FRACTION_DIGITS}
                                        placeholder={fleetStudio ? '—' : 'Non renseigné'}
                                        className={indicatorInputCls}
                                    />
                                    {fleetStudio && <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Longueur × largeur × hauteur intérieures</p>}
                                </section>}
                            </div>
                            {context === 'fleet' && hasVolumeDifference && <div role="status" className="space-y-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                                <p>Capacité déclarée historique : <Measure value={declaredVolume} from="m3" quantity="volume" />. {interiorVolume == null ? 'Renseignez les dimensions pour vérifier cette valeur.' : 'Cette valeur diffère du volume calculé à partir des dimensions.'}</p>
                                {interiorVolume != null && mode !== 'view' && <button type="button" onClick={() => handleUpdate({ capacity_volume: interiorVolume })} className="rounded-md px-2 py-1 font-semibold text-orange-700 underline decoration-orange-300 underline-offset-2 hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 dark:text-orange-300 dark:hover:bg-orange-900/40">Utiliser le volume calculé</button>}
                            </div>}
                        </div>}

                        {/* Weights Box */}
                        {(!fleetStudio || panelId === 'weights') && <div className="rounded-xl p-3.5 space-y-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                            {!fleetStudio && <div className="flex items-center gap-2 mb-1">
                                <Weight className="w-4 h-4 text-orange-500" />
                                <h3 className="font-semibold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300">Capacités & Poids</h3>
                            </div>}
                            <div className={fleetStudio ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
                                <div>
                                    <MeasurementInput {...indicatorAppearance}
                                        label="Poids à vide"
                                        quantity="mass"
                                        storageUnit="kg"
                                        value={vEmptyWeight}
                                        onValueChange={handleTareChange}
                                        min={0}
                                        className={indicatorInputCls}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                <div>
                                    <MeasurementInput {...indicatorAppearance}
                                        label={isContainer ? 'Masse brute maximale' : 'PTAC'}
                                        infoLabel={isContainer ? 'Informations sur la masse brute maximale' : 'Informations sur le PTAC'}
                                        info={isContainer ? 'Masse maximale du conteneur chargé, tare incluse' : 'Masse maximale autorisée du véhicule chargé, tare incluse'}
                                        quantity="mass"
                                        storageUnit="kg"
                                        value={vGrossWeight}
                                        onValueChange={handleGrossMassChange}
                                        min={0}
                                        className={indicatorInputCls}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                {isTractor && (
                                    <div className={fleetStudio ? 'col-span-2' : undefined}>
                                        <MeasurementInput {...indicatorAppearance}
                                            label="PTRA"
                                            quantity="mass"
                                            storageUnit="kg"
                                            value={vCombinationWeight}
                                            onValueChange={v => handleUpdate({
                                                max_combination_weight: v,
                                                mass_contract_version: 'vehicle_mass_contract.v1',
                                                capacity_weight: null,
                                            })}
                                            min={0}
                                            className={indicatorInputCls}
                                            disabled={mode === 'view'}
                                        />
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Masse maximale de l'ensemble tracteur + semi-remorque chargé. À renseigner depuis la plaque du tracteur : un profil indicatif ne fixe pas de PTRA.
                                        </p>
                                    </div>
                                )}
                                {!isTractor && (
                                    <div className={`rounded-xl border px-3 py-3 ${fleetStudio ? 'col-span-2 border-orange-200/60 bg-orange-50/30 dark:border-orange-700/40 dark:bg-orange-950/20' : 'border-slate-200 bg-white/70 dark:border-slate-700 dark:bg-slate-900/30'}`}>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Charge utile calculée</p>
                                        <p className={`mt-1 font-semibold tabular-nums tracking-tight text-slate-900 dark:text-slate-100 ${fleetStudio && derivedPayload != null ? 'text-2xl' : 'text-sm'}`}>
                                            {derivedPayload == null ? 'À calculer — renseignez tare et limite brute' : <Measure value={derivedPayload} from="kg" quantity="mass" />}
                                        </p>
                                        {derivedPayload != null && <p className="text-xs text-slate-500 dark:text-slate-400">PTAC / masse brute maximale − poids à vide</p>}
                                        {vGrossWeight != null && vEmptyWeight != null && derivedPayload == null && <p className="text-xs text-red-600 dark:text-red-400">La limite brute doit être supérieure ou égale au poids à vide.</p>}
                                        {context === 'fleet' && hasPayloadDifference && <p role="status" className="mt-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">Charge utile déclarée historique : <Measure value={declaredPayload} from="kg" quantity="mass" />. Valeur à vérifier, conservée tant que la tare et la limite brute ne sont pas modifiées.</p>}
                                        {context === 'session' && hasPayloadDifference && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">Charge utile retenue pour ce projet : <Measure value={declaredPayload} from="kg" quantity="mass" />. Vérifiez la tare et la limite brute pour la recalculer.</p>}
                                    </div>
                                )}
                            </div>
                        </div>}

                    </div>
                </>
            )
        } else if (panelId === 'pressure' && fleetStudio) {
            panelContent = <>
                <div data-panel-body data-vehicle-indicators="pressure" className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-slate-900 dark:text-slate-100">
                    <FloorPressureConfigurator length={vLength} width={vWidth} maxPressure={vMaxFloorPressure} cellSize={vPressureCellSize} disabled={mode === 'view'}
                        onMaxPressureChange={value => handleUpdate({ max_floor_pressure: value })} onCellSizeChange={value => handleUpdate({ pressure_cell_target_size: value })} />
                </div>
            </>
        } else if (panelId === 'advanced' && fleetStudio) {
            const targets = [
                { key: 'x' as const, label: 'Position longitudinale (X)', accessibleLabel: 'Position longitudinale du centre de gravité', value: Number(cgX), max: 1, start: '0 % (avant)', end: '100 % (arrière)', valueText: `${Math.round(Number(cgX) * 100)} % depuis l’avant` },
                { key: 'y' as const, label: 'Position transversale (Y)', accessibleLabel: 'Position transversale du centre de gravité', value: Number(cgY), max: 1, start: '0 % (gauche)', end: '100 % (droite)', valueText: `${Math.round(Number(cgY) * 100)} % depuis la gauche` },
                { key: 'deviation' as const, label: 'Déviation maximale tolérée', accessibleLabel: 'Déviation maximale du centre de gravité', value: Number(maxCGDev), max: .5, start: '0 % (strict)', end: '50 % (tolérant)', valueText: `Plus ou moins ${Math.round(Number(maxCGDev) * 100)} %` },
            ]
            panelContent = <div data-panel-body data-vehicle-indicators="advanced" className={`grid min-h-0 flex-1 grid-cols-1 content-start gap-3 overflow-y-auto p-3 text-slate-900 dark:text-slate-100 ${immersive ? '' : 'sm:grid-cols-3'}`}>
                {targets.map(target => <div key={target.key} data-cg-indicator={target.key} className={`${indicatorTileCls} !p-2 min-w-0 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-500/15`}>
                    <label htmlFor={`${railId}-cg-value-${target.key}`} className="block min-h-7 text-[11px] font-medium text-slate-500 dark:text-slate-400">{target.label}</label>
                    <div className="flex h-12 items-center justify-center gap-1">
                        {target.key === 'deviation' && <div className="w-[78px] shrink-0"><DeviationGauge value={target.value} showValue={false} /></div>}
                        <CGPercentageInput id={`${railId}-cg-value-${target.key}`} label={target.key === 'x' ? 'Position longitudinale en pourcentage' : target.key === 'y' ? 'Position transversale en pourcentage' : 'Déviation maximale en pourcentage'} value={target.value} max={target.max} deviation={target.key === 'deviation'} disabled={mode === 'view'} onChange={value => handleCGChange(target.key, value)} />
                    </div>
                    <input id={`${railId}-cg-${target.key}`} type="range" min={0} max={target.max} step={.01} value={target.value}
                        aria-label={target.accessibleLabel} aria-valuetext={target.valueText} disabled={mode === 'view'}
                        onChange={event => handleCGChange(target.key, Number(event.target.value))}
                        className="mt-1 h-4 w-full cursor-pointer accent-orange-500 disabled:cursor-default" />
                    <div aria-hidden="true" className="flex justify-between gap-2 text-[10px] text-slate-500 dark:text-slate-400"><span>{target.start}</span><span>{target.end}</span></div>
                    {target.key === 'deviation' && <p data-cg-volume-caption className="mt-1.5 text-[10px] leading-4 text-slate-500 dark:text-slate-400" title="Enveloppe visuelle des écarts longitudinaux et transversaux, bornée par le véhicule. La hauteur complète n’ajoute aucune tolérance verticale.">Volume 3D : enveloppe X/Y, hauteur complète.</p>}
                </div>)}
            </div>
        } else if (panelId === 'advanced') {
            panelContent = <>
                <PanelHeader hidden={fleetStudio} title="Paramètres avancés de chargement" icon={<Target className="h-4 w-4" />} onClose={() => togglePanel(panelId)} />
                <div data-panel-body className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-slate-900 dark:text-slate-100">
                    <p className="text-sm text-slate-600 dark:text-slate-300">Ces réglages influencent les objectifs et la précision des calculs de chargement. Conservez les valeurs connues de votre configuration.</p>
                        {/* CG Box */}
                        <div className="rounded-xl p-3.5 space-y-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Target className="w-4 h-4 text-orange-500" />
                                    <h3 className="font-semibold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300">Centre de Gravité Cible</h3>
                                </div>
                                <span className="text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-950/60 text-orange-600 dark:text-orange-400">CG</span>
                            </div>

                            {/* CG X */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-slate-600 dark:text-slate-300">Position Longitudinale (X)</span>
                                    <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-orange-500 text-white text-xs">
                                        {Math.round(Number(cgX) * 100)} %
                                    </span>
                                </div>
                                <label className="block text-xs">Saisie précise (%)<input type="number" aria-label="Position longitudinale en pourcentage" min={0} max={100} step={1} value={Math.round(Number(cgX) * 100)} disabled={mode === 'view'} onChange={event => { if (event.target.value !== '' && event.currentTarget.validity.valid) handleCGChange('x', Number(event.target.value) / 100) }} className={inputCls} /></label>
                                <input
                                    type="range"
                                    aria-label="Position longitudinale du centre de gravité"
                                    aria-valuetext={`${Math.round(Number(cgX) * 100)} % depuis l’avant`}
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={Number(cgX)}
                                    onChange={e => handleCGChange('x', parseFloat(e.target.value))}
                                    disabled={mode === 'view'}
                                    className="w-full h-6 rounded-lg cursor-pointer accent-orange-500"
                                />
                                <div className="flex justify-between text-xs font-mono text-slate-600 dark:text-slate-300">
                                    <span>0 % (avant)</span><span>50 % (centre)</span><span>100 % (arrière)</span>
                                </div>
                            </div>

                            {/* CG Y */}
                            <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-slate-600 dark:text-slate-300">Position Transversale (Y)</span>
                                    <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-indigo-600 text-white text-xs">
                                        {Math.round(Number(cgY) * 100)} %
                                    </span>
                                </div>
                                <label className="block text-xs">Saisie précise (%)<input type="number" aria-label="Position transversale en pourcentage" min={0} max={100} step={1} value={Math.round(Number(cgY) * 100)} disabled={mode === 'view'} onChange={event => { if (event.target.value !== '' && event.currentTarget.validity.valid) handleCGChange('y', Number(event.target.value) / 100) }} className={inputCls} /></label>
                                <input
                                    type="range"
                                    aria-label="Position transversale du centre de gravité"
                                    aria-valuetext={`${Math.round(Number(cgY) * 100)} % depuis la gauche`}
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={Number(cgY)}
                                    onChange={e => handleCGChange('y', parseFloat(e.target.value))}
                                    disabled={mode === 'view'}
                                    className="w-full h-6 rounded-lg cursor-pointer accent-indigo-600"
                                />
                                <div className="flex justify-between text-xs font-mono text-slate-600 dark:text-slate-300">
                                    <span>0 % (gauche)</span><span>50 % (centre)</span><span>100 % (droite)</span>
                                </div>
                            </div>

                            {/* Deviation */}
                            <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                        <Gauge className="w-3.5 h-3.5 text-slate-400" />
                                        Déviation Max Tolérée
                                    </span>
                                    <span className={`font-mono font-bold px-1.5 py-0.5 rounded text-xs ${Number(maxCGDev) > 0.2 ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                        }`}>
                                        ±{Math.round(Number(maxCGDev) * 100)} %
                                    </span>
                                </div>
                                <label className="block text-xs">Saisie précise (%)<input type="number" aria-label="Déviation maximale en pourcentage" min={0} max={50} step={1} value={Math.round(Number(maxCGDev) * 100)} disabled={mode === 'view'} onChange={event => { if (event.target.value !== '' && event.currentTarget.validity.valid) handleCGChange('deviation', Number(event.target.value) / 100) }} className={inputCls} /></label>
                                <input
                                    type="range"
                                    aria-label="Déviation maximale du centre de gravité"
                                    aria-valuetext={`Plus ou moins ${Math.round(Number(maxCGDev) * 100)} %`}
                                    step="0.01"
                                    min="0"
                                    max="0.5"
                                    value={Number(maxCGDev)}
                                    onChange={e => handleCGChange('deviation', parseFloat(e.target.value))}
                                    disabled={mode === 'view'}
                                    className="w-full h-6 rounded-lg cursor-pointer accent-orange-500"
                                />
                                <div className="flex justify-between text-xs font-mono text-slate-600 dark:text-slate-300">
                                    <span>0 % (strict)</span><span className="text-amber-700 dark:text-amber-300">20 %</span><span>50 % (tolérant)</span>
                                </div>
                            </div>
                        </div>

                    {!fleetStudio && pressureCellField}

                </div>
            </>
        } else if (panelId === 'constraints') {
            panelContent = (
                <>
                    <PanelHeader hidden={fleetStudio} title="Contraintes & Équipements" icon={<SlidersHorizontal className="w-4 h-4 text-orange-500" />} onClose={() => togglePanel(panelId)} />
                    <div data-panel-body data-vehicle-indicators={fleetStudio ? 'equipment' : undefined} className="p-4 space-y-4 overflow-y-auto flex-1">

                        {/* Pressure Limits */}
                        {!fleetStudio && <div className="rounded-xl p-3.5 space-y-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-1">
                                <CircleGauge className="w-4 h-4 text-orange-500" />
                                <h3 className="font-semibold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300">Pression Plancher</h3>
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                <div>
                                    {pressureLimitField}
                                </div>

                            </div>
                        </div>}

                        {/* Reefer Unit */}
                        <div className={fleetStudio ? 'space-y-3' : `rounded-xl p-3.5 space-y-3 border transition-colors ${vHasReefer ? 'bg-sky-50 dark:bg-sky-950/30 border-sky-300 dark:border-sky-800' : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'
                            }`}>
                            <Toggle
                                indicator={fleetStudio}
                                id="has_reefer_unit"
                                icon={<Thermometer className="h-4 w-4" />}
                                checked={vHasReefer}
                                onChange={v => {
                                    handleUpdate({
                                        has_reefer_unit: v,
                                        reefer_temperature_range: v ? (vReeferRange || { min: -20, max: 10, unit: 'C' }) : null,
                                    })
                                }}
                                label="Unité de Réfrigération"
                                description="Transport sous température contrôlée (frigo)"
                                disabled={mode === 'view'}
                            />
                            {vHasReefer && (
                                <div className={fleetStudio ? 'space-y-2' : 'pt-2 border-t border-sky-200 dark:border-sky-800/60'}>
                                    <RangeSlider
                                        indicator={fleetStudio}
                                        disabled={mode === 'view'}
                                        unit={vReeferRange?.unit ?? 'C'}
                                        min={vReeferRange?.min ?? -20}
                                        max={vReeferRange?.max ?? 10}
                                        onChange={(min, max) => {
                                            handleUpdate({
                                                reefer_temperature_range: { ...vReeferRange, min, max }
                                            })
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ADR Hazardous */}
                        <div className={fleetStudio ? 'space-y-3' : `rounded-xl p-3.5 border transition-colors ${vIsAdr ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800' : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'
                            }`}>
                            <Toggle
                                indicator={fleetStudio}
                                id="is_hazardous_compatible"
                                icon={<AlertTriangle className="h-4 w-4" />}
                                checked={vIsAdr}
                                onChange={v => handleUpdate({ is_hazardous_compatible: v })}
                                label="Compatible ADR (Matières Dangereuses)"
                                description="Compatibilité déclarée de l’équipement, à vérifier pour les marchandises transportées."
                                disabled={mode === 'view'}
                            />
                        </div>
                    </div>
                </>
            )
        } else if (panelId === 'axles') {
            panelContent = (
                <>
                    <PanelHeader hidden={fleetStudio} title="Essieux & attelage" icon={<Disc3 className="w-4 h-4 text-orange-500" />} onClose={() => togglePanel(panelId)} />
                    <div className="p-2.5 overflow-y-auto flex-1 space-y-2">
                        <AxleConfiguration
                            vehicle={{
                                ...vehicle,
                                dimensions: { length: vLength, width: vWidth, height: vHeight }
                            } as any}
                            onChange={(updated) => handleUpdate({ axle_configuration: updated.axle_configuration, fifth_wheel: updated.fifth_wheel })}
                            mode={mode}
                            couplingControls={(isTractor || vehicle?.type === 'SEMI_TRAILER' || vehicle?.vehicle_subtype?.startsWith('tracteur_')) && <FifthWheelConfig
                                vehicle={{
                                    ...vehicle,
                                    dimensions: { length: vLength, width: vWidth, height: vHeight }
                                } as any}
                                onChange={(updated) => handleUpdate({
                                    fifth_wheel: (updated as any).fifth_wheel,
                                    axle_configuration: (updated as any).axle_configuration,
                                })}
                                mode={mode}
                                isTractor={isTractor}
                            />}
                        />
                    </div>
                </>
            )
        } else if (panelId === 'obstacles' && !isTractor) {
            panelContent = (
                <>
                    <PanelHeader hidden={fleetStudio} title="Éditeur d'Obstacles" icon={<AlertOctagon className="w-4 h-4 text-orange-500" />} onClose={() => togglePanel(panelId)} />
                    <div data-obstacle-panel-body className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2.5">
                        <ObstacleEditor
                            selectedObstacleId={selectedObstacleId}
                            onSelectObstacle={setSelectedObstacleId}
                            vehicle={{
                                ...vehicle,
                                dimensions: { length: vLength, width: vWidth, height: vHeight }
                            } as any}
                            onChange={(updated) => handleUpdate({ obstacles: (updated as any).obstacles })}
                            mode={mode}
                            isTractor={isTractor}
                        />
                    </div>
                </>
            )
        }

        if (!panelContent) return null

        return (
            <div
                key={panelId}
                id={`${railId}-panel-${panelId}`}
                role="region"
                aria-label={`Configuration — ${menuItems.find(item => item.id === panelId)?.label}`}
                data-config-panel={panelId}
                className={immersive ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden' : accordion ? 'vehicle-accordion-content flex min-h-0 min-w-0 flex-1 flex-col' : `${fleetStudio ? 'vehicle-glass' : ''} pointer-events-auto flex min-h-0 flex-col overflow-hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl border ${fleetStudio ? 'border-slate-200/70 shadow-sm dark:border-slate-700' : 'border-slate-200 dark:border-slate-800 shadow-2xl'} ${widthClass}`}
                style={!immersive && fleetStudio && !accordion && fleetDashboard ? { height: '100%', maxHeight: '100%' } : undefined}
            >
                {panelContent}
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            onFocusCapture={event => {
                const target = event.target as HTMLElement
                if (!target.matches('input,select,textarea') || !target.closest('[data-config-panel]')) return
                requestAnimationFrame(() => { if (target === document.activeElement) target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }) })
            }}
            onInvalidCapture={event => {
                const input = event.target as HTMLInputElement
                const section = input.closest<HTMLElement>('[data-config-panel]')?.dataset.configPanel
                if (!section) return
                setActivePanel(section)
                requestAnimationFrame(() => { if (!input.validity.valid) input.focus() })
            }}
            data-equipment-immersive={immersive || undefined}
            className={`relative w-full overflow-hidden ${immersive ? 'equipment-immersive vehicle-fleet-config bg-slate-50 dark:bg-slate-950' : fleetStudio ? `vehicle-fleet-config bg-slate-50 dark:bg-slate-950 ${fleetDashboard ? 'vehicle-fleet-dashboard' : 'fleet-stacked'} ${visiblePanels.length === 1 ? 'fleet-single-panel' : ''}` : 'rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl'} ${className}`}
            style={{ height: typeof height === 'number' ? `${height}px` : height, minHeight: 0 }}
        >
            {/* Background 3D View */}
            <div data-vehicle-stage className={fleetStudio ? 'absolute inset-0 z-0' : 'absolute inset-0 bg-slate-950 z-0'} aria-hidden={sceneHidden}
                style={{ visibility: sceneHidden ? 'hidden' : undefined }}>
                <div ref={previewRef} className={fleetStudio ? 'absolute inset-0 overflow-hidden' : 'h-full'}>
                <IsometricPreview
                    presentation={fleetStudio ? 'studio' : 'default'}
                    toolbarEnd={immersive ? <button type="button" aria-label={cardsHidden ? 'Afficher les cartes' : 'Masquer les cartes'} aria-pressed={cardsHidden} onClick={() => { if (keepInvalidDraft(visiblePanels)) return; setCardsHidden(previous => !previous) }} className="vehicle-glass inline-flex h-[38px] items-center justify-center gap-2 rounded-xl border px-2.5 text-xs font-medium text-slate-600 shadow-sm dark:text-slate-200" title={cardsHidden ? 'Afficher les cartes' : 'Masquer les cartes'}>{cardsHidden ? <PanelsTopLeft size={16} /> : <EyeOff size={16} />}<span className="hidden xl:inline">{cardsHidden ? 'Afficher les cartes' : 'Masquer les cartes'}</span></button> : fleetStudio && context === 'fleet' && <div data-scene-favorites className="vehicle-glass flex h-[38px] items-center rounded-xl border border-slate-200/70 bg-white/90 px-1 shadow-sm dark:border-slate-700 dark:bg-slate-900 [&_[role=group]]:gap-0" title="Favoris de l’équipement"><StarRating rating={(vehicle as any)?.favorite_rating || 0} readonly={mode === 'view'} size="sm" onChange={rating => handleUpdate({ favorite_rating: rating })} /></div>}
                    studioInsets={immersive ? immersiveInsets : fleetStudio ? { left: fleetDashboard ? sceneLeft : 0, right: fleetDashboard ? sceneRight : 0, bottom: fleetDashboard && auxiliaryActive ? Math.max(68, dockHeight + 24) : 68, top: 12 } : undefined}
                    fullscreenTargetRef={immersive ? containerRef : undefined}
                    initialView={immersive ? 'iso' : undefined}
                    technicalView={visiblePanels.includes('obstacles') || visiblePanels.includes('advanced')}
                    targetActive={visiblePanels.includes('advanced')}
                    vehicle={{
                        ...vehicle,
                        isTractor,
                        dimensions: {
                            length: vLength ?? 12000,
                            width: vWidth ?? 2500,
                            height: vHeight ?? 2600,
                        },
                        target_cg_x: cgX,
                        target_cg_y: cgY,
                        max_cg_deviation: maxCGDev,
                        constraintEngineConfig: {
                            ...vehicle?.constraintEngineConfig,
                            maxCGDeviation: maxCGDev,
                            targetCG: {
                                x: cgX,
                                y: cgY,
                                z: vehicle?.constraintEngineConfig?.targetCG?.z ?? 0.5,
                            },
                        },
                    } as any}
                    tractor={tractor}
                    coupling={coupling}
                    width={fleetStudio ? previewSize.width : containerSize.width}
                    height={fleetStudio ? previewSize.height : containerSize.height}
                    isTractor={isTractor}
                />
                </div>
                {context === 'fleet' && [vLength, vWidth, vHeight].some(value => value == null || value <= 0) && <p role="status" className="vehicle-glass pointer-events-none absolute bottom-20 z-10 max-w-sm rounded-xl border border-orange-100 px-3 py-2 text-[11px] leading-5 text-orange-800" style={{ left: fleetDashboard ? sceneLeft : 12, right: fleetDashboard ? sceneRight : 12 }}>Aperçu indicatif : les dimensions manquantes ne sont pas enregistrées. Renseignez les dimensions réelles ou appliquez un modèle.</p>}
            </div>

            {immersive && <>
                {equipmentVisible && equipmentOverlay && <div ref={floatingEquipmentRef} data-floating-equipment className={`equipment-scene-selection absolute left-3 top-3 z-20 min-w-0 overflow-y-auto ${compactLayout ? 'right-3' : 'w-[272px]'}`} style={{ maxHeight: compactLayout ? '36%' : 'calc(100% - 96px)' }}>
                    {equipmentOverlay(compactLayout)}
                </div>}
                {!cardsHidden && activePanel && <div ref={floatingPanelRef} data-floating-settings className={`vehicle-glass equipment-scene-settings absolute right-3 z-20 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border shadow-lg ${compactLayout ? 'bottom-[76px] left-3' : 'top-3 w-[320px]'}`} style={compactLayout ? { height: 'min(44%, 300px)' } : { maxHeight: 'calc(100% - 88px)' }}
                    onKeyDown={event => { if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); selectImmersivePanel(null, true) } }}>
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200/60 px-3 py-2 dark:border-slate-700/60">
                        <div className="min-w-0"><h3 className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{menuItems.find(item => item.id === activePanel)?.label}</h3><p className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">{mode === 'view' ? 'Consultation seule' : 'Pour ce projet'}</p></div>
                        <button type="button" onClick={() => selectImmersivePanel(null, true)} aria-label="Fermer les réglages" title="Fermer les réglages" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><XMarkIcon aria-hidden="true" className="h-4 w-4" /></button>
                    </div>
                    {renderPanel(activePanel)}
                </div>}
                <nav aria-label="Sections de l’équipement" className="vehicle-glass absolute bottom-3 left-3 right-3 z-20 min-w-0 rounded-2xl border p-1.5 shadow-sm">
                    {compactLayout ? <>
                        <label className="sr-only" htmlFor={`${railId}-section`}>Section de configuration</label>
                        <select ref={sectionSelectRef} id={`${railId}-section`} data-rail-navigation value={cardsHidden ? 'preview' : activePanel ?? 'preview'} onChange={event => {
                            const selected = event.target.value
                            if (keepInvalidDraft(visiblePanels)) { event.currentTarget.value = cardsHidden ? 'preview' : activePanel ?? 'preview'; return }
                            selectImmersivePanel(selected === 'preview' ? null : selected)
                        }} className="min-h-11 w-full min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-800 dark:border-slate-700 dark:text-slate-100">
                            <option value="preview">Aperçu 3D · Équipement</option>
                            {menuItems.map(item => <option key={item.id} value={item.id}>{item.label} — {statusLabels[getPanelStatus(item.id)]}</option>)}
                        </select>
                    </> : <div className="flex items-center gap-1 overflow-x-auto">
                        {menuItems.map(item => {
                            const active = !cardsHidden && activePanel === item.id
                            return <button key={item.id} type="button" id={`${railId}-nav-${item.id}`} data-rail-navigation aria-label={item.label} aria-expanded={active} aria-controls={active ? `${railId}-panel-${item.id}` : undefined}
                                onClick={() => selectImmersivePanel(active ? null : item.id)}
                                className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${active ? 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200 dark:bg-orange-950/60 dark:text-orange-200 dark:ring-orange-800' : 'text-slate-600 hover:bg-white/60 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                                <item.icon aria-hidden="true" className="h-4 w-4 shrink-0" /><span>{item.label}</span>
                            </button>
                        })}
                    </div>}
                </nav>
            </>}

            {/* Floating Menu & Side Panels Overlay */}
            {!immersive && <div className={fleetStudio ? 'absolute z-10 flex min-h-0 flex-col gap-2.5 pointer-events-none' : compactLayout ? 'absolute inset-2 z-10 flex min-h-0 flex-col gap-2 pointer-events-none' : 'absolute right-4 top-4 bottom-4 flex flex-row-reverse gap-3 items-stretch z-10 pointer-events-none'}
                data-configuration-dock
                style={fleetStudio ? { top: fleetDashboard ? effectiveDockTop : 12, left: 12, right: fleetDashboard ? sceneRight : 12, bottom: compactLayout && containerSize.height < 300 ? 4 : 12 } : undefined}>

                {fleetDashboard && auxiliaryActive && <div role="separator" tabIndex={0} aria-label="Hauteur du panneau de configuration" aria-orientation="horizontal"
                    aria-controls={`${railId}-panel-${activePanel}`} aria-valuemin={Math.round(minDockHeight)} aria-valuemax={Math.round(maxDockHeight)} aria-valuenow={Math.round(dockHeight)} aria-valuetext={`${Math.round(dockHeight)} pixels`}
                    title="Glisser pour régler la hauteur · Flèches haut/bas au clavier · Double-clic pour réinitialiser"
                    data-panel-resize-handle className="pointer-events-auto order-first flex h-3 shrink-0 touch-none cursor-ns-resize items-center justify-center rounded-full text-slate-400 outline-none hover:bg-orange-100/70 hover:text-orange-600 focus-visible:ring-2 focus-visible:ring-orange-500 dark:hover:bg-orange-950/60"
                    onPointerDown={event => { if (event.button !== 0) return; event.preventDefault(); event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId); resizeGesture.current = { pointerId: event.pointerId, y: event.clientY, height: dockHeight } }}
                    onPointerMove={event => { const gesture = resizeGesture.current; if (gesture?.pointerId === event.pointerId) setRequestedDockHeight(Math.max(minDockHeight, Math.min(maxDockHeight, gesture.height + gesture.y - event.clientY))) }}
                    onPointerUp={event => { resizeGesture.current = null; if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId) }}
                    onLostPointerCapture={() => { resizeGesture.current = null }}
                    onPointerCancel={() => { resizeGesture.current = null }}
                    onDoubleClick={() => setRequestedDockHeight(null)}
                    onKeyDown={event => { if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return; event.preventDefault(); setRequestedDockHeight(event.key === 'Home' ? minDockHeight : event.key === 'End' ? maxDockHeight : Math.max(minDockHeight, Math.min(maxDockHeight, dockHeight + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 50 : 20)))) }}>
                    <GripHorizontal size={22} aria-hidden="true" />
                </div>}

                {compactLayout ? <div className={`pointer-events-auto shrink-0 rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900 ${fleetStudio ? 'vehicle-glass order-2' : ''}`}>
                    <label className="sr-only" htmlFor={`${railId}-section`}>Section de configuration</label>
                    <select data-rail-navigation ref={sectionSelectRef} id={`${railId}-section`} value={activePanel && visiblePanels.includes(activePanel) ? activePanel : 'preview'} onChange={event => {
                        if (keepInvalidDraft(visiblePanels)) { event.currentTarget.value = activePanel ?? 'preview'; return }
                        const selected = event.target.value
                        if (fleetStudio) setOpenFleetPanels(previous => selected === 'preview' ? [] : accordionIds.includes(selected) && !previous.includes(selected) ? [...previous, selected] : previous)
                        setActivePanel(selected === 'preview' ? null : selected)
                    }} className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white">
                        <option value="preview">Aperçu 3D</option>
                        {menuItems.map(item => <option key={item.id} value={item.id}>{item.label} — {statusLabels[getPanelStatus(item.id)]}</option>)}
                    </select>
                </div> : fleetStudio ? <nav aria-label="Sections de l’équipement" className="vehicle-glass pointer-events-auto order-2 flex h-11 shrink-0 items-center gap-1 overflow-x-auto rounded-xl border border-slate-200/70 bg-white/90 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                    {menuItems.filter(item => !accordionIds.includes(item.id)).map(item => {
                        const active = visiblePanels.includes(item.id), status = getPanelStatus(item.id)
                        return <button key={item.id} type="button" id={`${railId}-nav-${item.id}`} data-rail-navigation aria-label={item.label} aria-expanded={active} aria-controls={active ? `${railId}-panel-${item.id}` : undefined} aria-describedby={`${railId}-status-${item.id}`} onClick={() => togglePanel(item.id, false)}
                            className={`relative inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[11px] font-medium transition ${active ? 'bg-orange-50 text-orange-600 ring-1 ring-inset ring-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:ring-orange-800' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'}`}>
                            <item.icon size={14} aria-hidden="true" />{item.label}<span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${status === 'complete' ? 'bg-orange-500' : status === 'partial' ? 'bg-orange-400' : 'bg-slate-300 dark:bg-slate-500'}`} /><span id={`${railId}-status-${item.id}`} className="sr-only">{statusLabels[status]}</span>
                        </button>
                    })}
                </nav> : (

                <div className={`pointer-events-auto ${isMenuCollapsed ? 'w-[56px]' : 'w-[152px]'} bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-2xl flex flex-col h-full transition-[width] duration-200`}>
                    {/* Top badge */}
                    <div className={`border-b border-slate-100 dark:border-slate-800 flex flex-col items-center gap-2 ${isMenuCollapsed ? 'px-2 py-3' : 'px-3 pt-3 pb-2.5'}`}>
                        <button
                            type="button"
                            onClick={() => setIsMenuCollapsed(collapsed => !collapsed)}
                            title={isMenuCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
                            aria-label={isMenuCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
                            aria-expanded={!isMenuCollapsed}
                            className="w-8 h-8 rounded-lg bg-orange-500 text-white flex items-center justify-center shadow-md shadow-orange-500/30 hover:bg-orange-600 transition-colors"
                        >
                            {isMenuCollapsed
                                ? <PanelRightOpen className="w-4 h-4" aria-hidden="true" />
                                : <PanelRightClose className="w-4 h-4" aria-hidden="true" />}
                        </button>
                        {!isMenuCollapsed && (
                            <div className="w-full">
                                <div className="h-1.5 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${applicableSections > 0 ? (completedCount / applicableSections) * 100 : 0}%`,
                                            background: 'linear-gradient(90deg, #f97316, #f59e0b)'
                                        }}
                                    />
                                </div>
                                <p className="text-center text-[9px] font-mono mt-1 text-slate-500 dark:text-slate-400">
                                    {completedCount}/{applicableSections} renseigné{completedCount > 1 ? 's' : ''}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Navigation Items */}
                    <div className={`flex-1 space-y-1 overflow-y-auto ${isMenuCollapsed ? 'p-1.5' : 'p-2'}`}>
                        {menuItems.map((item) => {
                            const Icon = item.icon
                            const isActive = visiblePanels.includes(item.id)
                            const status = getPanelStatus(item.id)
                            const dotColor = {
                                complete: '#10b981',
                                partial: '#f59e0b',
                                empty: '#ef4444',
                                optional: '#94a3b8',
                                'not-applicable': '#94a3b8',
                            }[status]

                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    id={`${railId}-nav-${item.id}`}
                                    data-rail-navigation
                                    onClick={() => togglePanel(item.id, false)}
                                    title={item.label}
                                    aria-label={item.label}
                                    aria-expanded={visiblePanels.includes(item.id)}
                                    aria-controls={visiblePanels.includes(item.id) ? `${railId}-panel-${item.id}` : undefined}
                                    aria-describedby={`${railId}-status-${item.id}`}
                                    className={`w-full flex ${isMenuCollapsed ? 'items-center justify-center px-1.5 py-2' : 'flex-col items-start gap-1 px-2.5 py-2'} rounded-xl transition-all duration-150 relative text-left ${isActive
                                        ? 'bg-orange-700 text-white shadow-lg shadow-orange-500/25'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    {isActive && (
                                        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-6 bg-orange-300 rounded-r-full" />
                                    )}
                                    <div className={`flex items-center w-full ${isMenuCollapsed ? 'justify-center' : 'justify-between'}`}>
                                        <Icon className="w-4 h-4 flex-shrink-0" />
                                        {!isMenuCollapsed && (
                                            <span
                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ background: isActive ? 'rgba(255,255,255,0.85)' : dotColor }}
                                            />
                                        )}
                                    </div>
                                    {!isMenuCollapsed && (
                                        <span className={`text-xs font-semibold leading-snug break-words w-full ${isActive ? 'text-white' : 'text-slate-700 dark:text-slate-200'
                                            }`}>
                                            {item.label}
                                        </span>
                                    )}
                                    <span id={`${railId}-status-${item.id}`} className="sr-only">{statusLabels[status]}</span>
                                </button>
                            )
                        })}
                    </div>

                </div>
                )}

                {/* Active Side Panels */}
                <div className={fleetStudio ? 'order-1 flex min-h-0 flex-1 items-end gap-2.5' : 'contents'}>
                {visiblePanels.filter(id => !fleetStudio || !compactLayout && !accordionIds.includes(id)).map(renderPanel)}
                </div>
            </div>}
            {fleetStudio && !immersive && (
                <div data-fleet-panel-layout className={overviewVisible ? 'contents' : 'absolute right-3 z-10 flex min-h-0 min-w-0 flex-col gap-2 overflow-y-auto'}
                    style={overviewVisible ? undefined : fleetDashboard ? { top: 12, bottom: 12, width: sidebarWidth } : { left: 12, bottom: 64, top: containerSize.height < 300 ? 4 : Math.min(112, Math.round(containerSize.height * .22)) }}>
                {showOverview && <VehicleFleetOverview reference={vehicle?.registration_number} subtype={vehicle?.vehicle_subtype || (vehicle as any)?.container_type} type={vehicle?.type} status={(vehicle as any)?.status}
                    wide={overviewVisible} readOnly={mode === 'view'} reefer={vHasReefer} adr={vIsAdr} reeferRange={vReeferRange}
                    registrationRequired={registrationRequired} registrationFocusRequest={registrationFocusRequest} registrationErrorId={registrationErrorId}
                    allowCustomType={allowCustomType && !selectedPresetId} profileLabel={getAllPresets().find(preset => preset.id === selectedPresetId)?.label}
                    onChange={handleUpdate} onRequestProfile={onRequestProfile} profileTriggerRef={profileTriggerRef} />}
                <aside aria-label="Paramètres de chargement" data-loading-settings
                    className={overviewVisible ? 'absolute right-3 top-3 bottom-3 z-10 flex min-h-0 min-w-0 flex-col gap-2 overflow-y-auto' : fleetDashboard ? 'flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-y-auto' : 'flex min-w-0 shrink-0 flex-col gap-2'}
                    hidden={compactLayout && visiblePanels.length === 0}
                    style={{ ...(overviewVisible ? { width: sidebarWidth } : {}), display: compactLayout && visiblePanels.length === 0 ? 'none' : undefined }}>
                    {compactLayout && auxiliaryActive && renderPanel(activePanel!)}
                    {accordionIds.map(id => {
                        const item = menuItems.find(item => item.id === id)!
                        const open = openFleetPanels.includes(id)
                        const status = getPanelStatus(id)
                        return <section key={id} data-accordion-card={id}
                            className={`vehicle-glass pointer-events-auto flex min-w-0 shrink-0 flex-col rounded-2xl border p-3 shadow-sm ${fleetDashboard && open ? 'grow' : ''}`}>
                            <h3 className="shrink-0">
                                <button type="button" id={`${railId}-nav-${id}`} data-rail-navigation
                                    aria-label={item.label} aria-expanded={open}
                                    aria-controls={open ? `${railId}-panel-${id}` : undefined}
                                    aria-describedby={`${railId}-status-${id}`}
                                    onClick={() => togglePanel(id, false)}
                                    className="relative flex w-full items-center justify-between gap-3 rounded-lg text-left text-sm font-semibold text-slate-800 outline-none transition hover:text-orange-600 focus-visible:ring-2 focus-visible:ring-orange-500/30 dark:text-slate-100">
                                    <span className="inline-flex min-w-0 items-center gap-2"><item.icon size={16} aria-hidden="true" className={open ? 'shrink-0 text-orange-500' : 'shrink-0 text-slate-400'} />{item.label}</span>
                                    <ChevronDown size={16} aria-hidden="true" className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180 text-orange-500' : ''}`} />
                                    <span id={`${railId}-status-${id}`} className="sr-only">{statusLabels[status]}</span>
                                </button>
                            </h3>
                            {open && renderPanel(id)}
                        </section>
                    })}
                </aside>
                </div>
            )}
        </div>
    )
}

export default VehicleConfigRail
